import useResponsive from "../../../hooks/minimal/useResponsive";
import { useLocales } from "../../../locales";
import { useEffect, useState } from "react";
import { MotionContainer } from "../../../components/minimal/animate";
import { OrderDetails } from "./OrderDetails";
import { useEventContext } from "../../../context-state/EventContext";
import { PaymentService } from "../../../services/paymentService";
import { UserEventService } from "../../../services/userEventService";
import { SuccessMsg } from "./SuccessMsg";
import { TicketsTable } from "./TicketsTable";
import { CustomCard } from "./common";
import { useSearchParams } from "react-router-dom";
import { ErrorService } from "../../../services/errorService";
import { TicketLimitMsg } from "./TicketLimitMsg";
import { EventSeatsChart } from "./EventSeatsChart";
import { generateRandomNumber, wait } from "../../../utils/generic";
import { getRelevantPaymentFormError } from "../../../utils/transformers.js";
import { TicketLimitNoWaitingListMsg } from "./TicketLimitNoWaitingListMsg.jsx";
import facebookPixel from "../../../utils/facebookPixel";
import googleAnalytics from "../../../utils/googleAnalytics";
import { observer } from "mobx-react-lite";
import checkoutStore from "../../../stores/checkout.store";
import { NoTicketsLeftMsg } from "./NoTicketsLeftMsg";
import { NotEnoughTicketsLeftMsg } from "./NotEnoughTicketsLeftMsg";
import { useSnackbar } from "notistack";

export const Checkout = observer(({ onSuccessOrder, onFailedOrder }) => {

    const { translate } = useLocales()
    const { eventPageContext } = useEventContext()
    const { event, account, tickets, setCoupon, seasonTicket, setSeasonTicket, setAppliedCouponQs, setAppliedHrcQs } = eventPageContext
    const isDesktop = useResponsive('up', 'md');
    const [hasChosenTickets, setHasChosenTickets] = useState(false)
    const [paymentFormError, setPaymentFormError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [forceDisableBtn, setForceDisableBtn] = useState(false)
    const [searchParams] = useSearchParams();
    const utm = searchParams.get('utm')
    const { activeStep, setActiveStep } = checkoutStore
    const { chosenTickets, setChosenTickets } = checkoutStore
    const [paymeToken, setPaymeToken] = useState(null)
    const { enqueueSnackbar } = useSnackbar();

    // Why we do this?
    // Payme limit our quotas to 100 requests per second. in rush times like event which have 3k users want to purchase at the same time we might exceed this limit.
    // so we need to buffer the requests we send to them, therefore we randomly disable the pay button between 5 - 15 seconds for each user so they not all click again at the same time.
    const startErrorBufferTimer = async () => {
        setForceDisableBtn(true)
        let counter = generateRandomNumber();
        while (counter > 0) {
            const msgToPresent = `${translate('event_page.payment_rush_error_one')} ${counter} ${translate('event_page.payment_rush_error_two')}`
            setPaymentFormError(msgToPresent)
            counter--
            await wait(1000)
        }
        setForceDisableBtn(false)
        setPaymentFormError('')
    }

    /* Effect to check if there are chosen tickets */
    useEffect(() => {
        if (!chosenTickets) return
        let hasTickets = false
        for (const key of Object.keys(chosenTickets)) {
            if (chosenTickets[key].amount) {
                hasTickets = true
                break
            }
        }
        setHasChosenTickets(hasTickets)
    }, [chosenTickets])

    useEffect(() => {
        setCoupon(null)
        setSeasonTicket(null)
    }, [])

    useEffect(() => {
        if (activeStep === 1) {
            setCoupon(null)
            setSeasonTicket(null)
        }
    }, [activeStep])

    useEffect(() => {
        if (activeStep === 0) {
            setAppliedCouponQs(false);
            setAppliedHrcQs(false);
        }
    }, [activeStep])

    useEffect(() => {
        if (checkoutStore.splitPayment) {
            return
        }
        if (activeStep === 2) {
            // when the user switchs back to the order details and back to the payment we want to reset payme
            PaymentService.initPaymentForm(translate, account.sellerPublicKey)
            setPaymentFormError('')
            setPaymeToken(null)
        }
    }, [activeStep])

    const handleChosenTicketChanged = (ticketId, amount) => {
        const newState = { ...chosenTickets }
        newState[ticketId].amount = amount
        setChosenTickets(newState)
    }

    /* Final Method */
    const createOrder = async (orderDetails) => {
        const sellerIsPnedingApproval = account?.parent?.pendingApproval
        if (sellerIsPnedingApproval) {
            setPaymentFormError(translate('event_page.pending_approval_account_cannot_sell_tickets'))
            enqueueSnackbar(translate('event_page.pending_approval_account_cannot_sell_tickets'), { variant: 'error' })
            return
        }
        try {
            setPaymentFormError('')
            setIsLoading(true)
            const { userEvents, totalToPay, coupon, summary, totalToPayToSendPayMe } = orderDetails
            // The 'totalToPayToSendPayMe' is the 'totalToPay' but * 100 because this is how PayMe need to revise it
            const { allUserEvents, primary } = UserEventService.prepareUserEvents(userEvents, summary, event.inputData, coupon, tickets, seasonTicket)
            // Free ticket logic
            let purchaseToken
            if (totalToPay === 0) {
                purchaseToken = "FREE_TICKET"
            } else if (checkoutStore.splitPayment) {
                purchaseToken = "SPLIT_PAYMENT"
            } else if (paymeToken) {
                purchaseToken = paymeToken
            } else {
                const tokenizationData = { payerEmail: primary.userEvent.email, payerPhone: primary.userEvent.phone, total: totalToPayToSendPayMe }
                const tokenData = await PaymentService.preparePaymentToken(tokenizationData)
                purchaseToken = tokenData.token
                setPaymeToken(purchaseToken)
            }
            // Create userEvents
            const savedUserEvents = await UserEventService.createManyUserEvents({ allUserEvents, accountId: account._id, eventId: event._id })
            // Create final eventOrder
            const { data } = await UserEventService.createEventOrder({
                accountId: account._id,
                eventId: event._id,
                userEvents: savedUserEvents,
                purchaseToken,
                coupon,
                utm,
                seasonTicket,
                seller: account.parent?._id,
                marketplaceId: account.marketPlace?._id
            })
            // Check if already tickets sold (between the time the user got into the page and bought the tickets)
            if (data.isTicketLimitReached) {
                setActiveStep(event.isAllowWaitingList ? 4 : 5)
                return
            }

            if (purchaseToken === "SPLIT_PAYMENT") {
                checkoutStore.getSplitPaymentIframeLink(data)
                return
            }

            // On Success
            if (onSuccessOrder) {
                onSuccessOrder({ eventOrder: data, userEvents: savedUserEvents })
            }
            facebookPixel.track('Purchase', { value: totalToPay, currency: 'ILS', content_type: 'ticket', content_name: event.name })
            googleAnalytics.purchase({ value: totalToPay, currency: 'ILS', content_type: 'ticket', content_name: event.name })
            setActiveStep(3)
        } catch (e) {
            // IMPORTANT! - the 'e' is not really an error object! PayMe throw an object which is not always an error here...
            if (e.statusCode >= 430 && e.statusCode < 440) {
                setActiveStep(6)
            }

            if (onFailedOrder) {
                onFailedOrder(e)
            }
            console.error(e);
            const errorToRender = getRelevantPaymentFormError(e, translate)
            const hint = { type: 'payment-form-error', errorToRender }
            ErrorService.handleError(e, hint)
            if (!errorToRender) {
                startErrorBufferTimer()
                return
            }
            setPaymentFormError(errorToRender)
        } finally {
            setIsLoading(false)
        }
    }

    let stepToRender

    switch (activeStep) {
        case 0:
            stepToRender = event.isSeatsEvent
                ? <EventSeatsChart setChosenTickets={setChosenTickets} setActiveStep={setActiveStep} />
                : <TicketsTable event={event} tickets={tickets} handleChosenTicketChanged={handleChosenTicketChanged} chosenTickets={chosenTickets} setActiveStep={setActiveStep} hasChosenTickets={hasChosenTickets} />
            break
        case 1:
        case 2:
        case 6:
            stepToRender =
                <OrderDetails
                    isLoading={isLoading}
                    paymentFormError={paymentFormError}
                    createOrder={createOrder}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    chosenTickets={chosenTickets}
                    forceDisableBtn={forceDisableBtn}
                />
            break
        case 3:
            stepToRender = <SuccessMsg />
            break
        case 4:
            stepToRender = <TicketLimitMsg />
            break;
        case 5:
            stepToRender = <TicketLimitNoWaitingListMsg />
            break;
        case 7:
            stepToRender = <NoTicketsLeftMsg />
            break;
        case 8:
            stepToRender = <NotEnoughTicketsLeftMsg />
            break;
        default:
            stepToRender = <TicketsTable event={event} tickets={tickets} handleChosenTicketChanged={handleChosenTicketChanged} chosenTickets={chosenTickets} setActiveStep={setActiveStep} hasChosenTickets={hasChosenTickets} />
    }

    return (
        <CustomCard component={MotionContainer} sx={{ p: isDesktop ? 2 : 1, pb: isDesktop ? 2 : 3 }}>
            {stepToRender}
        </CustomCard>
    )
})
